import React from 'react';

/**
 * Template Renderer Utility for DataGrid Custom Templates
 * 
 * Supports template variables in the format {{field}} or {{row.field}}
 * Also supports nested object access like {{row.user.name}}
 * 
 * Example templates:
 * - "{{std_nm}} ({{department}})" → "John Doe (Computer Science)"
 * - "<span class='badge'>{{status}}</span>" → <span class='badge'>Active</span>
 * - "{{fee}} - {{score}}%" → "₹5000 - 85%"
 */

/**
 * Safely get nested property value from object
 */
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : '';
  }, obj);
};

/**
 * Replace template variables with actual values from row data
 */
const interpolateTemplate = (template: string, rowData: any): string => {
  return template.replace(/\{\{([^}]+)\}\}/g, (match, path) => {
    const trimmedPath = path.trim();
    
    // Handle {{row.field}} format
    if (trimmedPath.startsWith('row.')) {
      const fieldPath = trimmedPath.substring(4); // Remove 'row.'
      return getNestedValue(rowData, fieldPath) || '';
    }
    
    // Handle {{field}} format (direct field access)
    return getNestedValue(rowData, trimmedPath) || '';
  });
};

/**
 * Render template as HTML with row data interpolation
 */
export const renderTemplate = (template: string, rowData: any): React.ReactElement => {
  const interpolatedHtml = interpolateTemplate(template, rowData);
  
  return (
    <div 
      dangerouslySetInnerHTML={{ __html: interpolatedHtml }}
      className="template-content"
    />
  );
};

/**
 * Render template as plain text (for cases where HTML is not desired)
 */
export const renderTemplateText = (template: string, rowData: any): string => {
  return interpolateTemplate(template, rowData);
};

/**
 * Validate template syntax and return potential issues
 */
export const validateTemplate = (template: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Check for unmatched braces
  const openBraces = (template.match(/\{\{/g) || []).length;
  const closeBraces = (template.match(/\}\}/g) || []).length;
  
  if (openBraces !== closeBraces) {
    errors.push('Unmatched template braces {{ }}');
  }
  
  // Check for empty variables
  const emptyVariables = template.match(/\{\{\s*\}\}/g);
  if (emptyVariables) {
    errors.push('Empty template variables found');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Get all template variables from a template string
 */
export const extractTemplateVariables = (template: string): string[] => {
  const matches = template.match(/\{\{([^}]+)\}\}/g) || [];
  return matches.map(match => match.replace(/[{}]/g, '').trim());
};

/**
 * Common template examples for different use cases
 */
export const templateExamples = {
  // Basic field display
  nameWithDepartment: "{{std_nm}} ({{department}})",
  
  // HTML with styling
  statusBadge: "<span class='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-{{status === 'Active' ? 'green' : 'red'}}-100 text-{{status === 'Active' ? 'green' : 'red'}}-800'>{{status}}</span>",
  
  // Multiple fields with formatting
  studentInfo: "<div class='text-sm'><div class='font-medium text-gray-900'>{{std_nm}}</div><div class='text-gray-500'>{{department}} - Year {{year}}</div></div>",
  
  // Currency and percentage
  feeWithScore: "<div class='text-right'><div class='text-lg font-semibold text-green-600'>₹{{fee}}</div><div class='text-sm text-gray-500'>Score: {{score}}%</div></div>",
  
  // Contact information
  contactInfo: "<div class='text-sm'><div class='text-gray-900'>{{std_nm}}</div><div class='text-gray-500'>📞 {{mbl_no}}</div></div>",
  
  // Rating with stars
  ratingDisplay: "<div class='flex items-center'><span class='text-yellow-400'>{'★'.repeat({{rating}})}</span><span class='ml-1 text-sm text-gray-600'>({{rating}})</span></div>",
  
  // Conditional content
  scholarshipStatus: "{{is_scholarship ? '<span class=\"text-green-600 font-semibold\">🎓 Scholarship</span>' : '<span class=\"text-gray-500\">Regular</span>'}}",
  
  // Date formatting
  enrollmentInfo: "<div class='text-sm'><div>Enrolled: {{enrollment_date}}</div><div class='text-gray-500'>{{department}}</div></div>"
};
